import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { ImageModule } from 'primeng/image';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TutorService } from '../../../../modules/tutor/tutor-onboarding/tutor.service';
import { CONSTANTS } from '../../../../core/constants';

// Fixed validation check using start_year and end_year
export const yearRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startYear = control.get('start_year');
  const endYear = control.get('end_year');

  if (startYear && endYear && startYear.value && endYear.value && parseInt(startYear.value) > parseInt(endYear.value)) {
    return { yearRange: true };
  }

  return null;
};

@Component({
  selector: 'app-education-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    DropdownModule,
    TooltipModule,
    ImageModule,
    ToastModule
  ],
  templateUrl: './education-form.component.html',
  styleUrls: ['./education-form.component.scss']
})
export class EducationFormComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() editingItem: any = null; // Education object to edit, or null to add

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<any>();

  constants: any = CONSTANTS;
  tempEducationForm!: FormGroup;
  educationDocument: any = null;
  isBtnLoading: boolean = false;
  yearList: any[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _tutorService: TutorService,
    private messageService: MessageService
  ) {
    const startYear = 1990;
    const currentYear = new Date().getFullYear();
    for (let year = startYear; year <= currentYear; year++) {
      this.yearList.push({ name: year.toString(), value: year });
    }
  }

  ngOnInit(): void {
    this.prepareForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingItem'] && this.tempEducationForm) {
      this.prepareForm(this.editingItem);
    }
  }

  prepareForm(data: any = null) {
    this.tempEducationForm = this._formBuilder.group({
      id: [data?.id || ''],
      university_name: [data?.university_name || '', [Validators.required]],
      university_address: [data?.university_address || '', [Validators.required]],
      degree_type: [data?.degree_type || '', [Validators.required]],
      specialization: [data?.specialization || '', [Validators.required]],
      start_year: [data?.start_year || '', [Validators.required]],
      end_year: [data?.end_year || '', [Validators.required]],
      document: [data?.document || ''],
    }, { validators: yearRangeValidator });
    
    this.educationDocument = null;
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  saveEducationItem() {
    if (this.tempEducationForm.invalid) {
      this.tempEducationForm.markAllAsTouched();
      return;
    }

    this.isBtnLoading = true;
    const formValue = this.tempEducationForm.value;
    const formData = new FormData();
    formData.append('university_name', formValue.university_name);
    formData.append('university_address', formValue.university_address);
    formData.append('degree_type', formValue.degree_type);
    formData.append('specialization', formValue.specialization);
    formData.append('start_year', formValue.start_year.toString());
    formData.append('end_year', formValue.end_year.toString());

    if (this.educationDocument) {
      formData.append('file', this.educationDocument);
    }

    const saveObs$ = formValue.id
      ? this._tutorService.editEducation(formValue.id, formData)
      : this._tutorService.addEducation(formData);

    saveObs$.subscribe({
      next: (res: any) => {
        this.isBtnLoading = false;
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          this.saved.emit(res.data);
          this.closeDialog();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message });
        }
      },
      error: (err: any) => {
        this.isBtnLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Something went wrong' });
      }
    });
  }

  updateEducationDocument(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      if (!(file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png')) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please upload Image in JPEG or PNG format' });
        return;
      }

      this.educationDocument = file;
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.tempEducationForm.patchValue({ document: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }
}
